import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import ChannelsFilter from '@/components/ChannelsFilter';
import ExportButton from '@/components/ExportButton';
import FiltersDropdown from '@/components/FilterDropdown';
import FilterButton from '@/components/FilterDropdown/FilterButton';
import FilterRadioButton from '@/components/FilterDropdown/FilterRadioButton';
import {
  FilterGroupBody,
  FilterGroupHeader,
  FilterGroupTitle,
  FilterWrap,
} from '@/components/FilterDropdown/Parts';
import InfoTooltip from '@/components/InfoTooltip';
import {
  PageHeading,
  SectionHeading,
  TeamHeading,
} from '@/components/Layout/Parts';
import RestrictedContent from '@/components/RestrictedContent';
import {
  ResponsiveTable,
  SortableTh,
  Table,
  TableContent,
  Td,
  Th,
} from '@/components/Table';
import Text from '@/components/Text';
import useChannelsFilter from '@/hooks/useChannelsFilter';
import useHasPermission from '@/hooks/useHasPermission';
import { ChannelFinancesResource, SortState } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';
import { numFormatter } from '@/utils/helpers';

import FinancesTableRow from './components/FinancesTableRow';

export const Header = styled.div`
  display: flex;
  margin: 0 0 12px;
  align-items: flex-end;
`;

export const StyledTable = styled(Table)`
  th,
  td {
    &:nth-child(1) {
      width: 36%;
    }
    &:nth-child(2) {
      width: 16%;
    }
    &:nth-child(3) {
      width: 16%;
    }
    &:nth-child(4) {
      width: 16%;
    }
    &:nth-child(5) {
      width: 16%;
    }
  }
`;

const HeaderFilters = styled.div`
  display: flex;
  margin-bottom: 12px;
  justify-content: flex-end;
  gap: 8px;
`;

const AggregatedTable = styled(StyledTable)`
  margin-bottom: 16px;
`;

const Strong = styled.strong`
  font-weight: 700;
`;

export const LastUpdateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  height: 32px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.grayscaleDark};
  margin-right: 8px;
  padding: 0 12px;
`;

export const Dot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.success};
  margin-right: 8px;
`;

const StyledChannelsFilter = styled(ChannelsFilter)`
  margin-bottom: 24px;
`;

export const months = (() => {
  const months = [];

  let currentDate = DateTime.now();

  while (currentDate.diffNow('months').months > -18) {
    months.push(currentDate);
    currentDate = currentDate.minus({ months: 1 });
  }

  return months;
})();

export enum Periods {
  LAST_7_DAYS = '-7 days',
  LAST_30_DAYS = '-30 days',
  LAST_90_DAYS = '-90 days',
  LAST_YEAR = '-1 year',
  ALL_TIME = '',
}

export const renderPeriodTitle = (period: Periods) => {
  switch (period) {
    case Periods.LAST_7_DAYS:
      return 'Last 7 days';
    case Periods.LAST_30_DAYS:
      return 'Last 30 days';
    case Periods.LAST_90_DAYS:
      return 'Last 90 days';
    case Periods.LAST_YEAR:
      return 'Last year';
    default:
      return 'All time';
  }
};

export const getDateTimeObjectFromString = (period: string) => {
  const now = DateTime.now();

  switch (period) {
    case Periods.LAST_7_DAYS:
      return now.minus({
        days: 7,
      });
    case Periods.LAST_30_DAYS:
      return now.minus({
        days: 30,
      });
    case Periods.LAST_90_DAYS:
      return now.minus({
        days: 90,
      });
    case Periods.LAST_YEAR:
      return now.minus({
        years: 1,
      });
    default:
      return null;
  }
};

export interface FinancesData {
  channels: ChannelFinancesResource[];
  last_update?: string;
  totals: {
    published_revenue?: number;
    total_revenue?: number;
    total_views?: number;
    total_engaged_views?: number;
  };
}

const Finances: FC = () => {
  const { teamId } = useParams();

  const [filteredChannelsIds, filteredChannelsKey] = useChannelsFilter();

  const [sort, setSort] = useState<SortState>({
    direction: SortDirection.DESC,
    property: 'total_revenue',
  });

  const [activePeriod, setActivePeriod] = useState({
    type: 'month',
    period: months[0].toFormat('yyyy-LL-dd'),
  });

  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, isError } = useQuery<FinancesData>(
    [
      'finances',
      {
        teamId,
        sort,
        period: activePeriod.period,
        channelsKey: filteredChannelsKey,
      },
    ],
    () =>
      api.get<never, FinancesData>(`/v1/teams/${teamId}/finances`, {
        params: {
          ...activePeriod,
          sortDirection: sort.direction,
          sortProperty: sort.property,
          ...(filteredChannelsIds?.length && {
            channel_id: filteredChannelsIds,
          }),
        },
      }),
    {
      refetchOnMount: false,
      staleTime: 1000 * 120,
    },
  );

  const fetchCsv = () =>
    api.get<never, string>(`/v1/teams/${teamId}/finances/export`, {
      params: {
        ...activePeriod,
        sortDirection: sort.direction,
        sortProperty: sort.property,
        ...(filteredChannelsIds?.length && {
          channel_id: filteredChannelsIds,
        }),
      },
    });

  const userCanExportData = useHasPermission('view finances');

  const renderSectionHeading = () => {
    const { period, type } = activePeriod;

    if (type === 'month') {
      const dt = DateTime.fromFormat(period, 'yyyy-LL-dd');

      const startDate = dt.startOf('month').toFormat('dd/LL/yyyy');

      const endDate = dt.endOf('month').toFormat('dd/LL/yyyy');

      return `${dt.toFormat('yyyy LLLL')} (${startDate} - ${endDate})`;
    }

    const dt = getDateTimeObjectFromString(period);

    if (!dt) {
      return renderPeriodTitle(period as Periods);
    }

    const startDate = dt.toFormat('dd/LL/yyyy');

    const endDate = DateTime.now().toFormat('dd/LL/yyyy');

    return `${renderPeriodTitle(
      period as Periods,
    )} (${startDate} - ${endDate})`;
  };

  return (
    <>
      <TeamHeading />
      <PageHeading>Finances</PageHeading>
      <HeaderFilters>
        {!!data?.last_update && (
          <LastUpdateBadge>
            <Dot />
            <Text as="span" $type="smallHeadline">
              Last update:{' '}
              {DateTime.fromISO(data.last_update).toFormat('dd/LL/yyyy')}
            </Text>
          </LastUpdateBadge>
        )}
        {userCanExportData && (
          <ExportButton fetchCsv={fetchCsv} fileName={'finances.csv'} />
        )}
        <FilterWrap>
          <FilterButton
            isActive={showFilters}
            onClick={(e) => {
              e?.stopPropagation();
              setShowFilters((prev) => !prev);
            }}
            filterApplied
          />
          {showFilters && (
            <FiltersDropdown onClose={() => setShowFilters(false)}>
              <FilterGroupHeader>
                <FilterGroupTitle>By Period:</FilterGroupTitle>
              </FilterGroupHeader>
              <FilterGroupBody>
                {Object.values(Periods).map((period, index) => (
                  <FilterRadioButton
                    key={index}
                    id={`periodRadio${index}`}
                    value={period}
                    checked={activePeriod.period === period}
                    setValue={(newValue) =>
                      setActivePeriod({
                        period: newValue,
                        type: 'dateTimeString',
                      })
                    }
                  >
                    {renderPeriodTitle(period)}
                  </FilterRadioButton>
                ))}
              </FilterGroupBody>
              <FilterGroupHeader>
                <FilterGroupTitle>By Month:</FilterGroupTitle>
              </FilterGroupHeader>
              <FilterGroupBody>
                {months.map((dt, index) => (
                  <FilterRadioButton
                    key={index}
                    id={`monthRadio${index}`}
                    value={dt.toFormat('yyyy-LL-dd')}
                    checked={activePeriod.period === dt.toFormat('yyyy-LL-dd')}
                    setValue={(newValue) =>
                      setActivePeriod({
                        period: newValue,
                        type: 'month',
                      })
                    }
                  >
                    {dt.toFormat('yyyy LLLL')}
                  </FilterRadioButton>
                ))}
              </FilterGroupBody>
            </FiltersDropdown>
          )}
        </FilterWrap>
      </HeaderFilters>
      <StyledChannelsFilter onlyOwnedChannels />
      <SectionHeading>{renderSectionHeading()}</SectionHeading>
      <ResponsiveTable>
        <AggregatedTable $fixed>
          <thead>
            <tr>
              <Th>All Channels</Th>
              <Th>
                Total Revenue
                <InfoTooltip
                  content="Total revenue generated by selected channel in the selected period from videos published in any period"
                  title="Total Revenue"
                />
              </Th>
              <Th>
                Published Revenue
                <InfoTooltip
                  content="Total revenue generated by selected channel in the selected period from videos published in the selected period"
                  title="Published Revenue"
                />
              </Th>
              <Th>Total Views</Th>
              <Th>
                Engaged Views
                <InfoTooltip
                  content="Views counted under YouTube's engaged views metric (the pre-August 2026 counting logic that monetization is based on). Youtube channels only"
                  title="Engaged Views"
                />
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              colSpan={5}
              isLoading={isLoading}
              isError={isError}
              hasData={!!data?.channels?.length}
            >
              <tr>
                <Td>
                  {activePeriod.type === 'month' ? (
                    <>
                      Month:{' '}
                      <Strong>
                        {DateTime.fromFormat(
                          activePeriod.period,
                          'yyyy-LL-dd',
                        ).toFormat('LLLL')}
                      </Strong>
                    </>
                  ) : (
                    <>
                      <Strong>
                        {renderPeriodTitle(activePeriod.period as Periods)}
                      </Strong>
                    </>
                  )}
                </Td>
                <Td>
                  <RestrictedContent permission="view finances">
                    {data?.totals.total_revenue
                      ? `$ ${numFormatter(data.totals.total_revenue, 1, 2)}`
                      : '-'}
                  </RestrictedContent>
                </Td>
                <Td>
                  <RestrictedContent permission="view finances">
                    {data?.totals.published_revenue
                      ? `$ ${numFormatter(data.totals.published_revenue, 1, 2)}`
                      : '-'}
                  </RestrictedContent>
                </Td>
                <Td>
                  {data?.totals.total_views
                    ? numFormatter(data.totals.total_views)
                    : '-'}
                </Td>
                <Td>
                  {data?.totals.total_engaged_views
                    ? numFormatter(data.totals.total_engaged_views)
                    : '-'}
                </Td>
              </tr>
            </TableContent>
          </tbody>
        </AggregatedTable>
      </ResponsiveTable>
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>
                Channel name
                <Text as="div" $type="extraSmall" $color="gray">
                  Channel link
                </Text>
              </Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                disableDefault
                property="total_revenue"
                title="Total Revenue"
              >
                <InfoTooltip
                  content="Total revenue generated by selected channel in the selected period from videos published in any period"
                  title="Total Revenue"
                />
              </SortableTh>
              <SortableTh
                setSort={setSort}
                sort={sort}
                disableDefault
                property="published_revenue"
                title="Published Revenue"
              >
                <InfoTooltip
                  content="Total revenue generated by selected channel in the selected period from videos published in the selected period"
                  title="Published Revenue"
                />
              </SortableTh>
              <SortableTh
                setSort={setSort}
                sort={sort}
                disableDefault
                property="total_views"
                title="Total Views"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                disableDefault
                property="total_engaged_views"
                title="Engaged Views"
              >
                <InfoTooltip
                  content="Views counted under YouTube's engaged views metric (the pre-August 2026 counting logic that monetization is based on). Youtube channels only"
                  title="Engaged Views"
                />
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            <TableContent
              colSpan={5}
              isLoading={isLoading}
              isError={isError}
              hasData={!!data?.channels?.length}
            >
              {data?.channels?.map((channel) => (
                <FinancesTableRow key={channel.id} channel={channel} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
    </>
  );
};

export default Finances;
